import Pusher from "pusher-js";
import config from "../config";
import XanoAPI from "./XanoAPI";

class PusherService {
  private static instance: PusherService;
  private pusher: Pusher | null = null;
  private api: XanoAPI;

  private constructor() {
    this.api = XanoAPI.getInstance();
  }

  public static getInstance(): PusherService {
    if (!PusherService.instance) {
      PusherService.instance = new PusherService();
    }
    return PusherService.instance;
  }

  public initialize(): Pusher {
    if (!this.pusher) {
      console.log(
        "Initializing Pusher connection (private channels with authentication)..."
      );

      this.pusher = new Pusher(config.PUSHER_APP_KEY, {
        cluster: config.PUSHER_CLUSTER,
        // Connection options
        enabledTransports: ["ws", "wss"],
        disabledTransports: [],
        // Custom authorizer for private channels
        // Calls our Xano /messages/authenticate endpoint
        authorizer: (channel) => {
          return {
            authorize: async (socketId: string, callback: any) => {
              try {
                // Enhanced debug logging
                console.log("🔐 Pusher Auth Request:");
                console.log("   Channel:", channel.name);
                console.log("   Socket ID:", socketId);
                console.log("   Using XanoAPI for authentication");

                // Use XanoAPI to authenticate (it handles tokens properly)
                // CRITICAL: Pass channel name for proper signature generation
                const data = await this.api.authenticatePusherChannel(socketId, channel.name);

                console.log("✅ Auth successful!");
                console.log("   Channel:", data.channel_name);
                console.log("   Auth string:", data.auth?.substring(0, 50) + "...");

                // Pusher expects: { auth: "key:signature" }
                callback(null, { auth: data.auth });
              } catch (error: any) {
                console.error("❌ Auth request error:", error);
                console.error("❌ Auth error details:", {
                  message: error?.message,
                  status: error?.status,
                  response: error?.response,
                });
                callback(error, null);
              }
            },
          };
        },
      });

      // Connection state monitoring
      this.pusher.connection.bind("connected", () => {
        console.log("✓ Pusher connected successfully");
      });

      this.pusher.connection.bind("error", (error: any) => {
        console.error("Pusher connection error:", error);
        // Log more details about the error
        if (error?.data) {
          console.error("Pusher error details:", {
            type: error.type,
            code: error.data?.code,
            message: error.data?.message,
          });
        }
      });
      
      // Listen for subscription errors on channels
      this.pusher.connection.bind("pusher:error", (error: any) => {
        console.error("Pusher error event:", error);
      });

      this.pusher.connection.bind("state_change", (states: any) => {
        console.log(`Pusher state: ${states.previous} → ${states.current}`);
      });
    }
    return this.pusher;
  }

  public getPusher(): Pusher | null {
    return this.pusher;
  }

  public subscribe(channelName: string) {
    if (!this.pusher) {
      this.initialize();
    }
    return this.pusher!.subscribe(channelName);
  }

  public unsubscribe(channelName: string) {
    if (this.pusher) {
      this.pusher.unsubscribe(channelName);
    }
  }

  public disconnect() {
    if (this.pusher) {
      this.pusher.disconnect();
      this.pusher = null;
    }
  }

  // Helper method to bind events with proper typing
  public bindEvent<T = any>(
    channelName: string,
    eventName: string,
    callback: (data: T) => void
  ) {
    const channel = this.subscribe(channelName);
    channel.bind(eventName, callback);
    return () => channel.unbind(eventName, callback);
  }

  // Test Pusher connection (for debugging only)
  public testConnection(): void {
    if (!this.pusher) {
      this.initialize();
    }
    console.log("Pusher connection state:", this.pusher?.connection.state);
  }
}

export default PusherService;
